import { createApiRef, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { z } from 'zod/v3';
import {
  radarConfigSchema,
  radarDataSchema,
  radarItemListSchema,
  radarItemSchema,
  radarSourcesSchema,
} from '@internal/plugin-tech-radar-aoe-common';
import type {
  RadarConfig,
  RadarData,
  RadarItem,
  RadarSource,
} from '@internal/plugin-tech-radar-aoe-common';

export type {
  RadarConfig,
  RadarData,
  RadarItem,
  RadarSource,
} from '@internal/plugin-tech-radar-aoe-common';

export type {
  RadarFlag,
  RadarQuadrant,
  RadarRevision,
  RadarRing,
  RadarSocial,
  RadarTechCapability,
} from '@internal/plugin-tech-radar-aoe-common';

const stringArraySchema = z.array(z.string());

const parseOrThrow = <T>(schema: z.ZodType<T>, payload: unknown, context: string): T => {
  const result = schema.safeParse(payload);
  if (!result.success) {
    throw new Error(`${context}: ${result.error.message}`);
  }
  return result.data;
};

export const techRadarAoeApiRef = createApiRef<TechRadarAoeApi>({
  id: 'plugin.tech-radar-aoe.api',
});

export interface TechRadarAoeApi {
  getSources(): Promise<RadarSource[]>;
  getRadarConfig(source?: string): Promise<RadarConfig>;
  getRadarData(source?: string): Promise<RadarData>;
  getItemById(id: string, source?: string): Promise<RadarItem | undefined>;
  getItemsByTag(tag: string, source?: string): Promise<RadarItem[]>;
  getAvailableVersions(source: string): Promise<string[]>;
}

export class TechRadarAoeApiClient implements TechRadarAoeApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getSources(): Promise<RadarSource[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('tech-radar-aoe');
    const response = await this.fetchApi.fetch(`${baseUrl}/sources`);

    if (!response.ok) {
      throw new Error(`Failed to fetch radar sources: ${response.statusText}`);
    }

    const payload = await response.json();
    return parseOrThrow(radarSourcesSchema, payload, 'Invalid radar sources payload');
  }

  async getRadarConfig(source?: string): Promise<RadarConfig> {
    const baseUrl = await this.discoveryApi.getBaseUrl('tech-radar-aoe');
    const url = new URL(`${baseUrl}/config`);
    if (source) {
      url.searchParams.set('source', source);
    }

    const response = await this.fetchApi.fetch(url.toString());

    if (!response.ok) {
      throw new Error(`Failed to fetch radar config: ${response.statusText}`);
    }

    const payload = await response.json();
    return parseOrThrow(radarConfigSchema, payload, 'Invalid radar config payload');
  }

  async getRadarData(source?: string): Promise<RadarData> {
    const baseUrl = await this.discoveryApi.getBaseUrl('tech-radar-aoe');
    const url = new URL(`${baseUrl}/data`);
    if (source) {
      url.searchParams.set('source', source);
    }

    const response = await this.fetchApi.fetch(url.toString());

    if (!response.ok) {
      throw new Error(`Failed to fetch radar data: ${response.statusText}`);
    }

    const payload = await response.json();
    return parseOrThrow(radarDataSchema, payload, 'Invalid radar data payload');
  }

  async getItemById(id: string, source?: string): Promise<RadarItem | undefined> {
    const baseUrl = await this.discoveryApi.getBaseUrl('tech-radar-aoe');
    const url = new URL(`${baseUrl}/items/${encodeURIComponent(id)}`);
    if (source) {
      url.searchParams.set('source', source);
    }

    const response = await this.fetchApi.fetch(url.toString());

    if (response.status === 404) {
      return undefined;
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch item: ${response.statusText}`);
    }

    const payload = await response.json();
    return parseOrThrow(radarItemSchema, payload, 'Invalid radar item payload');
  }

  async getItemsByTag(tag: string, source?: string): Promise<RadarItem[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('tech-radar-aoe');
    const url = new URL(`${baseUrl}/items`);
    url.searchParams.set('tag', tag);
    if (source) {
      url.searchParams.set('source', source);
    }

    const response = await this.fetchApi.fetch(url.toString());

    if (!response.ok) {
      throw new Error(`Failed to fetch items by tag: ${response.statusText}`);
    }

    const payload = await response.json();
    return parseOrThrow(radarItemListSchema, payload, 'Invalid items-by-tag payload');
  }

  async getAvailableVersions(source: string): Promise<string[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('tech-radar-aoe');
    const response = await this.fetchApi.fetch(`${baseUrl}/versions/${source}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch radar versions: ${response.statusText}`);
    }

    const payload = await response.json();
    return parseOrThrow(stringArraySchema, payload, 'Invalid radar versions payload');
  }
}
